import React from 'react';
import styles from '../styles/BackButton.module.css';

/**
 * Shared back affordance (UX-02).
 *
 * Follows the project's Unified Component Pattern: one component, several
 * presentation variants selected with `variant`, so every "go back" control in
 * the app has identical markup, tap target and accessible name.
 *
 * Accessibility contract:
 *  - always a real <button type="button"> (never a div/anchor)
 *  - the visible text is always "← Back"; the destination lives in the
 *    aria-label ("Back to Chat"), so screen-reader users get the specific
 *    target without every header rendering a different string
 *  - the "←" glyph is aria-hidden so AT doesn't announce "left arrow"
 *  - min hit area is --tap-min (44px) per WCAG 2.5.8
 *  - deliberately does NOT set `outline: none` — tokens.css owns the global
 *    :focus-visible ring
 *
 * Props:
 *  - onClick   (required) handler
 *  - label/to  destination name, e.g. "Chat" -> aria-label="Back to Chat".
 *              Omit for a plain aria-label="Back".
 *  - variant   "header" (default, light surfaces)
 *              | "onDark" (brand/dark-filled headers)
 *              | "pill"   (bordered chip-style headers)
 *  - className extra class for local layout tweaks (alignment, margins)
 */

const VARIANT_CLASS = {
  header: styles.header,
  onDark: styles.onDark,
  pill: styles.pill,
};

function BackButton({
  onClick,
  label,
  to,
  variant = 'header',
  className = '',
  disabled = false,
  ...rest
}) {
  const destination = label || to;
  const ariaLabel = destination ? `Back to ${destination}` : 'Back';

  const classes = [
    styles.backButton,
    VARIANT_CLASS[variant] || VARIANT_CLASS.header,
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      {...rest}
    >
      <span className={styles.glyph} aria-hidden="true">←</span>
      <span className={styles.text}>Back</span>
    </button>
  );
}

export default BackButton;
